/**
 * Universal 2D WALL-geometry utility — wall endpoints, nearest-wall search,
 * and wall-endpoint/placement snapping. Works everywhere; no Three.js.
 *
 * It does NOT describe how OBJECTS attach to walls. It used to carry a second
 * `OBJECT_CONFIGS` table for that, which had drifted from the real one
 * (`components/3d/utils/objectTypeConfigs.js`) — different `wallOffset` for
 * `toilet` and `mirror` — while being unreachable: the only functions that
 * read it had no callers anywhere in the app. The table and those three
 * functions (`OBJECT_CONFIGS`, `getObjectConfig`, `snapToWall`,
 * `requiresWall`) were deleted rather than pointed at the surviving table, so
 * that reviving any of them forces a conscious choice of values instead of
 * silently inheriting different ones.
 */

import { wallOutlineEnds } from '@shared-lib/geometry/wallOutlineEnds';
import { apply3DRotationTo2D } from './geometryUtils';

/**
 * Get wall endpoints from wall data
 * Handles multiple formats:
 * 1. polygonCorners - the two corners farthest apart in plan (preferred for polygon walls)
 * 2. start/end - use directly
 * 3. center/rotation/dimensions - calculate endpoints
 */
export function getWallEndpoints(wall) {
  // If wall has polygon corners, read its ends off the outline — which is
  // NOT a rectangle for a structure wall (a corner at every junction) or a
  // stepped room wall. See `wallOutlineEnds`.
  const outlineEnds = wallOutlineEnds(wall.polygonCorners);
  if (outlineEnds) {
    const ends = [...outlineEnds];

    // Sort by X then Z to get consistent start/end ordering
    ends.sort((a, b) => {
      if (Math.abs(a.x - b.x) > 0.001) return a.x - b.x;
      return a.z - b.z;
    });

    return {
      start: { x: ends[0].x, z: ends[0].z },
      end: { x: ends[1].x, z: ends[1].z },
    };
  }

  // If wall already has start/end, use them
  if (wall.start && wall.end) {
    return { start: wall.start, end: wall.end };
  }

  // Calculate from center + rotation + dimensions
  if (!wall.center || !wall.dimensions) {
    console.warn('Wall missing center or dimensions:', wall);
    return null;
  }

  const halfLength = (wall.dimensions.x || wall.dimensions[0] || 1) / 2;

  // Use apply3DRotationTo2D to handle rotation
  const [dirX, dirZ] = apply3DRotationTo2D(
    wall.rotation || { x: 0, y: 0, z: 0 }
  );

  return {
    start: {
      x: wall.center.x - dirX * halfLength,
      z: wall.center.z - dirZ * halfLength,
    },
    end: {
      x: wall.center.x + dirX * halfLength,
      z: wall.center.z + dirZ * halfLength,
    },
  };
}

/**
 * Find the nearest wall in a specific direction (behind an object)
 * Pure 2D geometry - for objects that already have a rotation
 *
 * @param {Object} point - Point with x and z coordinates
 * @param {number} facingAngle - Angle the object is facing (in radians)
 * @param {Array} walls - Array of wall objects
 * @param {number} maxDistance - Maximum distance to consider (default 1.5m)
 * @returns {Object|null} Wall info with point, normal, distance, facingAngle
 */
export function findWallBehindObject(
  point,
  facingAngle,
  walls,
  maxDistance = 1.5
) {
  if (!walls || walls.length === 0) {
    return null;
  }

  // Calculate the direction the object is facing
  const facingDir = {
    x: Math.sin(facingAngle),
    z: Math.cos(facingAngle),
  };

  // We want to find walls in the OPPOSITE direction (behind the object)
  const searchDir = {
    x: -facingDir.x,
    z: -facingDir.z,
  };

  let closestWall = null;
  let minDistance = maxDistance;
  let snapPoint = null;
  let wallNormal = null;

  for (const wall of walls) {
    // Get wall endpoints (handles both formats)
    const endpoints = getWallEndpoints(wall);
    if (!endpoints) continue;

    const { start, end } = endpoints;

    // Wall vector
    const wallVec = {
      x: end.x - start.x,
      z: end.z - start.z,
    };
    const wallLength = Math.sqrt(wallVec.x ** 2 + wallVec.z ** 2);

    if (wallLength === 0) continue; // Skip zero-length walls

    // Normalize wall direction
    const wallDir = {
      x: wallVec.x / wallLength,
      z: wallVec.z / wallLength,
    };

    // Vector from wall start to point
    const toPoint = {
      x: point.x - start.x,
      z: point.z - start.z,
    };

    // Project point onto wall line
    const projection = toPoint.x * wallDir.x + toPoint.z * wallDir.z;

    // Clamp projection to wall segment bounds
    const clampedProj = Math.max(0, Math.min(wallLength, projection));

    // Find closest point on wall segment
    const closest = {
      x: start.x + wallDir.x * clampedProj,
      z: start.z + wallDir.z * clampedProj,
    };

    // Calculate vector from point to closest point on wall
    const toWall = {
      x: closest.x - point.x,
      z: closest.z - point.z,
    };

    const dist = Math.sqrt(toWall.x ** 2 + toWall.z ** 2);

    if (dist === 0) continue; // Point is on the wall

    // Check if this wall is in the search direction (behind the object)
    // Normalize toWall vector
    const toWallNorm = {
      x: toWall.x / dist,
      z: toWall.z / dist,
    };

    // Dot product: > 0 means wall is in the search direction
    const dotProduct = toWallNorm.x * searchDir.x + toWallNorm.z * searchDir.z;

    // If wall has room center info, only allow snapping from the room-interior side
    if (wall._roomCenter) {
      const normal = { x: -wallDir.z, z: wallDir.x };
      const toRoomCenter = {
        x: wall._roomCenter.x - closest.x,
        z: wall._roomCenter.z - closest.z,
      };
      const roomCenterDot =
        toRoomCenter.x * normal.x + toRoomCenter.z * normal.z;
      const ptDot =
        (point.x - closest.x) * normal.x + (point.z - closest.z) * normal.z;
      if (roomCenterDot * ptDot < 0) {
        continue;
      }
    }

    // Only consider walls that are behind the object (dot product > 0.5 means within ~60 degrees)
    if (dotProduct > 0.5 && dist < minDistance) {
      minDistance = dist;
      closestWall = wall;
      snapPoint = closest;

      // Calculate wall normal (perpendicular to wall)
      // In 2D, perpendicular to (x, z) is (-z, x)
      wallNormal = { x: -wallDir.z, z: wallDir.x };

      // Ensure normal points toward the object
      if (wallNormal.x * toWall.x + wallNormal.z * toWall.z > 0) {
        wallNormal.x = -wallNormal.x;
        wallNormal.z = -wallNormal.z;
      }
    }
  }

  if (!closestWall) {
    return null;
  }

  // Return the wall info (keep the original facingAngle)
  return {
    wall: closestWall,
    point: snapPoint,
    distance: minDistance,
    normal: wallNormal,
    facingAngle, // Keep the object's original facing angle
  };
}

/**
 * Find the nearest wall to a point
 * Pure 2D geometry - fast and accurate
 *
 * @param {Object} point - Point with x and z coordinates
 * @param {Array} walls - Array of wall objects
 * @param {number} maxDistance - Maximum distance to consider (default 1.5m)
 * @returns {Object|null} Wall info with point, normal, distance, facingAngle
 */
export function findNearestWall(point, walls, maxDistance = 1.5) {
  if (!walls || walls.length === 0) {
    return null;
  }

  let closestWall = null;
  let minDistance = maxDistance;
  let snapPoint = null;
  let wallNormal = null;

  for (const wall of walls) {
    // Get wall endpoints (handles both formats)
    const endpoints = getWallEndpoints(wall);
    if (!endpoints) continue;

    const { start, end } = endpoints;

    // Wall vector
    const wallVec = {
      x: end.x - start.x,
      z: end.z - start.z,
    };
    const wallLength = Math.sqrt(wallVec.x ** 2 + wallVec.z ** 2);

    if (wallLength === 0) continue; // Skip zero-length walls

    // Normalize wall direction
    const wallDir = {
      x: wallVec.x / wallLength,
      z: wallVec.z / wallLength,
    };

    // Vector from wall start to point
    const toPoint = {
      x: point.x - start.x,
      z: point.z - start.z,
    };

    // Project point onto wall line
    const projection = toPoint.x * wallDir.x + toPoint.z * wallDir.z;

    // Clamp projection to wall segment bounds
    const clampedProj = Math.max(0, Math.min(wallLength, projection));

    // Find closest point on wall segment
    const closest = {
      x: start.x + wallDir.x * clampedProj,
      z: start.z + wallDir.z * clampedProj,
    };

    // Calculate distance from point to closest point on wall
    const dx = point.x - closest.x;
    const dz = point.z - closest.z;
    const dist = Math.sqrt(dx ** 2 + dz ** 2);

    // If wall has room center info, only allow snapping from the room-interior side
    if (wall._roomCenter) {
      // Wall normal (perpendicular to wall direction)
      const normal = { x: -wallDir.z, z: wallDir.x };
      const toRoomCenter = {
        x: wall._roomCenter.x - closest.x,
        z: wall._roomCenter.z - closest.z,
      };
      const roomCenterDot =
        toRoomCenter.x * normal.x + toRoomCenter.z * normal.z;
      const cursorDot = dx * normal.x + dz * normal.z;
      // Skip if cursor is on opposite side from room center (exterior side)
      if (roomCenterDot * cursorDot < 0) {
        continue;
      }
    }

    // Check if this is the closest wall
    if (dist < minDistance) {
      minDistance = dist;
      closestWall = wall;
      snapPoint = closest;

      // Calculate wall normal (perpendicular to wall)
      // In 2D, perpendicular to (x, z) is (-z, x)
      wallNormal = { x: -wallDir.z, z: wallDir.x };

      // Ensure normal points toward the object
      if (wallNormal.x * dx + wallNormal.z * dz < 0) {
        wallNormal.x = -wallNormal.x;
        wallNormal.z = -wallNormal.z;
      }
    }
  }

  if (!closestWall) {
    return null;
  }

  // Calculate facing angle from wall normal
  const facingAngle = Math.atan2(wallNormal.x, wallNormal.z);

  return {
    wall: closestWall,
    point: snapPoint,
    distance: minDistance,
    normal: wallNormal,
    facingAngle, // Angle to face away from wall
  };
}

/**
 * Adjust position when swapping models of different depths
 * Useful when replacing a fixture with a different GLB model
 *
 * @param {Object} params
 * @param {Object} params.currentPosition - Current object position { x, z }
 * @param {number} params.facingAngle - Angle object faces away from wall
 * @param {number} params.oldDepth - Original object depth
 * @param {number} params.newDepth - New object depth
 * @returns {Object} Adjusted position { x, z }
 */
export function adjustPositionForDepthChange({
  currentPosition,
  facingAngle,
  oldDepth,
  newDepth,
}) {
  // Calculate wall normal from facing angle
  const wallNormal = {
    x: Math.sin(facingAngle),
    z: Math.cos(facingAngle),
  };

  // Calculate depth difference
  // If new object is deeper, move it OUT (away from wall)
  // If new object is shallower, move it IN (toward wall)
  const depthDifference = (newDepth - oldDepth) / 2;

  return {
    x: currentPosition.x + wallNormal.x * depthDifference,
    z: currentPosition.z + wallNormal.z * depthDifference,
  };
}

/**
 * Snap a wall endpoint to the surface or end caps of nearby walls.
 * Creates perpendicular T-junction connections (surface snap) or allows
 * extending/turning at wall endpoints (end cap snap).
 *
 * @param {Object} point - Point to snap { x, z }
 * @param {Array} walls - Array of walls to snap to
 * @param {Object} options - Snap options
 * @param {number} options.snapDistance - Max distance for snap (default 0.15m / ~6 inches)
 * @param {string} options.excludeWallId - Wall ID to exclude (when editing, exclude itself)
 * @param {number} options.wallThickness - Default thickness of target walls for surface offset (default 0.1143m)
 * @param {number} options.newWallThickness - Thickness of the wall being placed/edited, for edge margin (default 0.15m)
 * @returns {Object|null} Snap result with point, angles, wall info, and snapType ('surface' or 'endcap')
 */
export function snapWallEndpointToWall(
  point,
  walls,
  {
    snapDistance = 0.15,
    excludeWallId = null,
    wallThickness = 0.1143,
    newWallThickness = 0.15,
  } = {}
) {
  if (!walls || walls.length === 0 || !point) {
    return null;
  }

  let closestSnap = null;
  let minDistance = snapDistance;

  for (const wall of walls) {
    // Skip the wall we're editing
    if (excludeWallId && wall.id === excludeWallId) {
      continue;
    }

    // Get wall endpoints
    const endpoints = getWallEndpoints(wall);
    if (!endpoints) continue;

    const { start, end } = endpoints;

    // Wall vector and length
    const wallVec = {
      x: end.x - start.x,
      z: end.z - start.z,
    };
    const wallLength = Math.sqrt(wallVec.x ** 2 + wallVec.z ** 2);

    if (wallLength === 0) continue;

    // Normalize wall direction
    const wallDir = {
      x: wallVec.x / wallLength,
      z: wallVec.z / wallLength,
    };

    // Wall normal (perpendicular, pointing to one side)
    const wallNormal = { x: -wallDir.z, z: wallDir.x };

    // Target wall's actual thickness
    const targetWallThickness = wall.dimensions?.z || wallThickness;
    const halfThickness = targetWallThickness / 2;

    // Wall direction angle (for extending)
    const wallAngle = Math.atan2(-wallDir.z, wallDir.x);

    // ---- Check for END CAP snapping (start and end points of wall) ----
    // End caps are the short edges at each end of the wall
    const endcapPoints = [
      {
        pos: start,
        type: 'start',
        outwardDir: { x: -wallDir.x, z: -wallDir.z },
      },
      { pos: end, type: 'end', outwardDir: { x: wallDir.x, z: wallDir.z } },
    ];

    // Half thickness of the new wall being placed
    const newWallHalfThickness = newWallThickness / 2;

    for (const endcap of endcapPoints) {
      // Distance from point to the end cap center (wall centerline endpoint)
      const dx = point.x - endcap.pos.x;
      const dz = point.z - endcap.pos.z;
      const distToEndcap = Math.sqrt(dx * dx + dz * dz);

      // Account for wall thickness - snap point should be at the end of the wall
      // The actual end cap surface is halfThickness beyond the centerline endpoint
      const distToEndcapSurface = distToEndcap - halfThickness;

      if (distToEndcapSurface >= 0 && distToEndcapSurface < minDistance) {
        minDistance = distToEndcapSurface;

        // For end caps, the extending angle follows the wall direction
        // The outward direction points away from the wall
        const extendingAngle = Math.atan2(
          -endcap.outwardDir.z,
          endcap.outwardDir.x
        );

        // Perpendicular angles (two options: turn left or turn right)
        const perpendicularAngle1 = extendingAngle + Math.PI / 2;
        const perpendicularAngle2 = extendingAngle - Math.PI / 2;

        // Calculate snap point for EXTENDING (walls connect end-to-end)
        // Walls connect at their centerline endpoints - no thickness offset needed
        // The end caps are perpendicular to the wall length, so they meet at the centerline
        const extendSnapPoint = {
          x: endcap.pos.x,
          z: endcap.pos.z,
        };

        // Calculate snap points for CORNER turns (perpendicular walls)
        // The new wall should cover the entire end cap, so snap to the FAR corner
        // This way the wall extends ACROSS the end cap (not away from it)
        //
        // For corner geometry:
        // - Offset outward by newWallHalfThickness so new wall's FACE is flush with end cap
        // - Offset perpendicular by existingWallHalfThickness to reach the corner of the end cap
        //
        // Determine which corner: snap to the OPPOSITE side from where user approaches
        const perpOffset = dx * wallNormal.x + dz * wallNormal.z;
        const cornerSign = perpOffset >= 0 ? -1 : 1; // Flip: snap to far corner

        // Corner snap point: new wall face flush with end cap, at the far corner
        const cornerSnapPoint = {
          x:
            endcap.pos.x +
            endcap.outwardDir.x * newWallHalfThickness +
            wallNormal.x * halfThickness * cornerSign,
          z:
            endcap.pos.z +
            endcap.outwardDir.z * newWallHalfThickness +
            wallNormal.z * halfThickness * cornerSign,
        };

        closestSnap = {
          // Default point is for extending - will be adjusted in snapWallPlacementToWall for corners
          point: extendSnapPoint,
          // Primary angle: extend in same direction as the wall
          perpendicularAngle: extendingAngle,
          // Additional info for end cap snaps
          extendingAngle,
          perpendicularAngles: [perpendicularAngle1, perpendicularAngle2],
          wall,
          wallNormal: endcap.outwardDir,
          distance: distToEndcapSurface,
          targetWallDir: wallDir,
          snapType: 'endcap',
          endcapType: endcap.type, // 'start' or 'end'
          // Include both snap points so caller can choose
          extendSnapPoint,
          cornerSnapPoint,
          // Include geometry info for corner calculations
          endcapCenterlinePos: endcap.pos,
          existingWallHalfThickness: halfThickness,
          existingWallNormal: wallNormal,
          cornerSign,
        };
      }
    }

    // ---- Check for SURFACE snapping (long edge of wall) ----
    // Vector from wall start to point
    const toPoint = {
      x: point.x - start.x,
      z: point.z - start.z,
    };

    // Project point onto wall line (distance along wall)
    const projectionAlongWall = toPoint.x * wallDir.x + toPoint.z * wallDir.z;

    // Edge margin: half the new wall's thickness so it ends up flush at corners
    const edgeMargin = newWallThickness / 2;

    // Check if projection is within wall bounds (with tolerance for snap detection)
    const tolerance = 0.05; // 5cm tolerance for detecting nearby walls
    if (
      projectionAlongWall < -tolerance ||
      projectionAlongWall > wallLength + tolerance
    ) {
      continue; // Point projects outside the wall segment - skip surface snap
    }

    // Clamp projection to wall bounds, keeping edge margin so new wall is flush at corners
    const clampedProjection = Math.max(
      edgeMargin,
      Math.min(wallLength - edgeMargin, projectionAlongWall)
    );

    // Calculate perpendicular distance (signed, based on which side of wall)
    const perpDistance = toPoint.x * wallNormal.x + toPoint.z * wallNormal.z;
    const absPerpDistance = Math.abs(perpDistance);

    // Account for wall thickness - snap to the wall surface, not centerline
    const distanceToSurface = absPerpDistance - halfThickness;

    // Check if within snap distance of the wall surface
    if (distanceToSurface > 0 && distanceToSurface < minDistance) {
      minDistance = distanceToSurface;

      // Calculate snap point on the wall surface
      // Move from wall centerline to the surface on the side the point is on
      const sideSign = perpDistance > 0 ? 1 : -1;
      const pointOnCenterline = {
        x: start.x + wallDir.x * clampedProjection,
        z: start.z + wallDir.z * clampedProjection,
      };
      const snapPoint = {
        x: pointOnCenterline.x + wallNormal.x * halfThickness * sideSign,
        z: pointOnCenterline.z + wallNormal.z * halfThickness * sideSign,
      };

      // Perpendicular angle - the new wall should be perpendicular to the target wall
      // Wall direction angle + 90 degrees
      const perpendicularAngle =
        wallAngle + (sideSign > 0 ? Math.PI / 2 : -Math.PI / 2);

      closestSnap = {
        point: snapPoint,
        perpendicularAngle,
        wall,
        wallNormal: {
          x: wallNormal.x * sideSign,
          z: wallNormal.z * sideSign,
        },
        distance: distanceToSurface,
        targetWallDir: wallDir,
        snapType: 'surface',
      };
    }
  }

  return closestSnap;
}

/**
 * Snap a wall being placed to nearby walls during placement preview.
 * Supports two snap types:
 * 1. Surface snap: T-junction where new wall is perpendicular to existing wall
 * 2. End cap snap: Extend or turn corner at the end of an existing wall
 *
 * @param {Object} wallPreview - Wall being placed { center: {x, z}, rotation: number, length: number }
 * @param {Array} walls - Array of walls to snap to
 * @param {Object} options - Snap options
 * @param {number} options.snapDistance - Max distance for snap (default 0.3m / ~1 foot)
 * @param {number} options.wallThickness - Thickness of walls (default 0.15m)
 * @param {number} options.perpendicularTolerance - Angle tolerance in radians (default ~30 degrees)
 * @returns {Object|null} { center: {x, z}, snappedEndpoint: 'start'|'end', wall: Object, snapType: 'surface'|'endcap' } or null
 */
export function snapWallPlacementToWall(
  wallPreview,
  walls,
  {
    snapDistance = 0.3,
    wallThickness = 0.15,
    perpendicularTolerance = Math.PI / 6,
  } = {}
) {
  if (!walls || walls.length === 0 || !wallPreview) {
    return null;
  }

  const { center, rotation, length } = wallPreview;
  if (!center || length === undefined) return null;

  // Calculate wall direction from rotation
  const wallDirX = Math.cos(rotation);
  const wallDirZ = -Math.sin(rotation);

  const halfLength = length / 2;

  // Calculate both endpoints
  const startPoint = {
    x: center.x - wallDirX * halfLength,
    z: center.z - wallDirZ * halfLength,
  };
  const endPoint = {
    x: center.x + wallDirX * halfLength,
    z: center.z + wallDirZ * halfLength,
  };

  // Try snapping each endpoint
  const endpoints = [
    { point: startPoint, type: 'start' },
    { point: endPoint, type: 'end' },
  ];

  let bestSnap = null;
  let bestDistance = snapDistance;

  for (const { point, type } of endpoints) {
    const snapResult = snapWallEndpointToWall(point, walls, {
      snapDistance,
      newWallThickness: wallThickness, // The wall being placed
    });

    if (snapResult && snapResult.distance < bestDistance) {
      const targetWallDir = snapResult.targetWallDir;
      if (!targetWallDir) continue;

      // Calculate dot product between new wall direction and target wall direction
      const dotProduct = Math.abs(
        wallDirX * targetWallDir.x + wallDirZ * targetWallDir.z
      );

      // For surface snaps: must be roughly perpendicular (dotProduct near 0)
      // For end cap snaps: can be either perpendicular OR parallel (extending)
      const isRoughlyPerpendicular =
        dotProduct < Math.sin(perpendicularTolerance);
      const isRoughlyParallel = dotProduct > Math.cos(perpendicularTolerance);

      let shouldSnap = false;

      if (snapResult.snapType === 'endcap') {
        // End cap snaps allow both extending (parallel) and corner turns (perpendicular)
        shouldSnap = isRoughlyPerpendicular || isRoughlyParallel;
      } else {
        // Surface snaps only allow perpendicular (T-junction)
        shouldSnap = isRoughlyPerpendicular;
      }

      if (shouldSnap) {
        bestDistance = snapResult.distance;

        // Choose the appropriate snap point based on snap type and wall orientation
        let snappedPoint;
        if (snapResult.snapType === 'endcap') {
          // For end cap snaps, choose between extend and corner snap points
          if (isRoughlyParallel) {
            // Extending: use the extend snap point
            snappedPoint = snapResult.extendSnapPoint || snapResult.point;
          } else {
            // Corner turn: use the corner snap point
            snappedPoint = snapResult.cornerSnapPoint || snapResult.point;
          }
        } else {
          // Surface snaps use the standard snap point
          snappedPoint = snapResult.point;
        }

        // Calculate new center based on snapped endpoint
        let newCenter;

        if (type === 'start') {
          // Start was snapped, calculate center from snapped start
          newCenter = {
            x: snappedPoint.x + wallDirX * halfLength,
            z: snappedPoint.z + wallDirZ * halfLength,
          };
        } else {
          // End was snapped, calculate center from snapped end
          newCenter = {
            x: snappedPoint.x - wallDirX * halfLength,
            z: snappedPoint.z - wallDirZ * halfLength,
          };
        }

        bestSnap = {
          center: newCenter,
          snappedEndpoint: type,
          snappedPoint,
          wall: snapResult.wall,
          perpendicularAngle: snapResult.perpendicularAngle,
          snapType: snapResult.snapType,
          // For end cap snaps, include additional angle options
          extendingAngle: snapResult.extendingAngle,
          perpendicularAngles: snapResult.perpendicularAngles,
          endcapType: snapResult.endcapType,
          // Include whether this is an extend or corner snap
          isExtending: snapResult.snapType === 'endcap' && isRoughlyParallel,
          isCorner: snapResult.snapType === 'endcap' && isRoughlyPerpendicular,
        };
      }
    }
  }

  return bestSnap;
}
